/**
 * @file VideoSelectionPage.tsx
 *
 * @description A React component that provides an interface for police officers to browse, filter, select,
 * and process bodycam video evidence. It supports pagination, date filtering, search functionality,
 * and video selection management.
 *
 * @module Views
 */
import React, { useMemo, useState } from "react";
import { Box, Container, Tab, Tabs, alpha, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@workos-inc/authkit-react";
import { useQuery } from "@tanstack/react-query";
import { usePageHeader } from "../../context/LayoutContext";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import { WizardStepper } from "../../shared/ui";
import { VideoFilters } from "./components/VideoFilters";
import { SelectionControls } from "./components/SelectionControls";
import { InstructionBanner } from "./components/InstructionBanner";
import { VideoGrid } from "./components/VideoGrid";
import { EvidenceTab, useListVideos } from "./hooks/useListVideos";
import { useVideoSelection } from "./hooks/useVideoSelection";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { canManageEvidenceShares } from "../../shared/auth/authorization";
import { useOrgId } from "../../shared/auth/orgContext";
import { stationDirectoryQuery } from "../../features/users/api/usersQueries";
import { ShareEvidenceModal } from "../../features/evidence/components/ShareEvidenceModal";
import { ShareMultipleEvidenceModal } from "../../features/evidence/components/ShareMultipleEvidenceModal";

const VideoSelectionPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [, setIsNavigating] = useState(false);
  const [activeTab, setActiveTab] = useState<EvidenceTab>("ingested");

  // Share dialog state: a single-video share (by id) or the batch "Share all".
  const [shareVideoId, setShareVideoId] = useState<string | null>(null);
  const [shareAllOpen, setShareAllOpen] = useState(false);

  const permissions = usePermissions();
  const { user } = useAuth();

  // Data fetching and state
  const {
    videos,
    isLoading,
    isInitialLoad,
    createdFrom,
    setCreatedFrom,
    createdTo,
    setCreatedTo,
    recordedFrom,
    setRecordedFrom,
    recordedTo,
    setRecordedTo,
    sortBy,
    sortOrder,
    requestSort,
    resetSort,
    fetchVideos,
    markVideoFetching,
    loadMore,
    hasMore,
    searchTerm,
    setSearchTerm,
    handleSearchChange,
    selectedUploaders,
    setSelectedUploaders,
  } = useListVideos(activeTab);

  // Selection and filtering state
  const {
    selectedVideos,
    handleVideoSelection,
    handleRemoveVideo,
    clearSelection,
  } = useVideoSelection();

  // The "Uploaded By" filter is available on the Ready to use and Evidence
  // Shared With Me tabs.
  const showUploaderFilter = activeTab === "ingested" || activeTab === "shared";

  // Officer directory for the uploader filter options (name shown, id sent).
  const organizationId = useOrgId();
  const directory = useQuery({
    ...stationDirectoryQuery(organizationId ?? ""),
    enabled: showUploaderFilter && !!organizationId,
  });
  const uploaderOptions = useMemo(() => {
    const users = directory.data?.users ?? [];
    return (
      users
        // Invitation-only rows (id null) have no account, so no uploads.
        .flatMap((u) =>
          u.id
            ? [
                {
                  name: `${u.firstName} ${u.lastName}`.trim() || u.email || u.id,
                  id: u.id,
                },
              ]
            : [],
        )
        // On the Shared With Me tab the current user's uploads are excluded, so
        // they should never appear as a filterable uploader.
        .filter((u) => (activeTab === "shared" ? u.id !== user?.id : true))
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  }, [directory.data, activeTab, user?.id]);

  // Set page header in app bar
  usePageHeader({
    title: "Video Evidence Locker",
    subtitle:
      "Browse, filter, and select bodycam footage for report generation",
    icon: <VideoLibraryIcon />,
    // Until the first load settles, show a wave indicator instead of the
    // (still-zero) count.
    chip: {
      label: isInitialLoad
        ? activeTab === "unfetched"
          ? "Other footage"
          : "Videos"
        : activeTab === "unfetched"
          ? `${videos.length} Other footage`
          : `${videos.length} Videos`,
      loading: isInitialLoad,
    },
  });

  // Actions
  const handleResetFilters = () => {
    setCreatedFrom(null);
    setCreatedTo(null);
    setRecordedFrom(null);
    setRecordedTo(null);
    setSearchTerm("");
    setSelectedUploaders([]);
    resetSort();
  };

  /**
   * @name handleProcessVideos
   *
   * @description Processes selected videos and navigates to the report generation page;
   * For multiple videos, IDs are joined with a tilde (~) in the URL
   */
  const handleProcessVideos = async () => {
    if (selectedVideos.length === 0) return;
    try {
      setIsNavigating(true);
      if (selectedVideos.length === 1) {
        navigate(`/generate-report/${selectedVideos[0]}`);
      } else {
        const videoIdsPath = selectedVideos.join("~");
        navigate(`/generate-report/${videoIdsPath}`);
      }
    } catch (error) {
      console.error("Error navigating with videos:", error);
    } finally {
      setIsNavigating(false);
    }
  };

  // A video's shares can be managed by its uploader or any station-wide reader
  // — the same gate the share endpoints enforce.
  const canShareVideo = React.useCallback(
    (videoId: string): boolean => {
      const video = videos.find((v) => v.id === videoId);
      if (!video) return false;
      return canManageEvidenceShares(
        permissions,
        !!video.uploadedBy && video.uploadedBy === user?.id,
      );
    },
    [videos, permissions, user?.id],
  );

  // Selected videos the current user is allowed to share (drives "Share all").
  const shareableSelectedVideos = useMemo(
    () =>
      selectedVideos
        .map((id) => videos.find((v) => v.id === id))
        .filter(
          (v): v is (typeof videos)[number] => !!v && canShareVideo(v.id),
        ),
    [selectedVideos, videos, canShareVideo],
  );

  // The single-video share target, resolved to its preview for the dialog title.
  const shareVideo = shareVideoId
    ? videos.find((v) => v.id === shareVideoId)
    : undefined;

  return (
    <Container
      sx={{ minWidth: { xs: "200px", sm: "450px" }, overflow: "auto", py: 2 }}
    >
      <WizardStepper
        activeStep={0}
        back={{ label: "My Reports", to: "/" }}
      />
      <Grid
        container
        spacing={3}
        direction="column"
        sx={{
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        {/* Filters Section */}
        <VideoFilters
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          createdFrom={createdFrom}
          setCreatedFrom={setCreatedFrom}
          createdTo={createdTo}
          setCreatedTo={setCreatedTo}
          recordedFrom={recordedFrom}
          setRecordedFrom={setRecordedFrom}
          recordedTo={recordedTo}
          setRecordedTo={setRecordedTo}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={requestSort}
          onClearSearch={() => setSearchTerm("")}
          disabled={activeTab === "unfetched"}
          showUploaderFilter={showUploaderFilter}
          uploaderOptions={uploaderOptions}
          selectedUploaders={selectedUploaders}
          onUploaderChange={setSelectedUploaders}
        />

        {/* Ingested vs. not-fetched (AI-filtered) evidence tabs */}
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_e, v) => {
                const next = v as EvidenceTab;
                if (next === activeTab) return;
                // Clear selection so IDs picked in the previous tab aren't
                // submitted by handleProcessVideos after switching.
                clearSelection();
                setActiveTab(next);
              }}
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab
                value="ingested"
                label="Ready to use"
                sx={{ textTransform: "none", fontWeight: 600 }}
              />
              <Tab
                value="mine"
                label="My Evidence"
                sx={{ textTransform: "none", fontWeight: 600 }}
              />
              <Tab
                value="shared"
                label="Evidence Shared With Me"
                sx={{ textTransform: "none", fontWeight: 600 }}
              />
              <Tab
                value="unfetched"
                label="Other footage"
                sx={{ textTransform: "none", fontWeight: 600 }}
              />
            </Tabs>
          </Box>
        </Grid>

        {/* Selection, upload, and sharing apply to every ready-to-use slice
            (Ready to use / My Evidence / Shared With Me) — not to the
            AI-filtered "Other footage" tab. */}
        {activeTab !== "unfetched" && (
          <>
            {/* Selected videos and actions */}
            <SelectionControls
              selectedVideos={selectedVideos}
              allVideos={videos}
              onRemoveVideo={handleRemoveVideo}
              onClearSelection={clearSelection}
              onProcessVideos={handleProcessVideos}
              fetchVideos={fetchVideos}
              allowUpload={true}
              onShareVideo={(id) => setShareVideoId(id)}
              onShareAll={() => {
                // With a single shareable video, use the same single-video
                // dialog as everywhere else so the "Shared with" list is shown.
                // The batch dialog is only for sharing several videos at once.
                if (shareableSelectedVideos.length === 1) {
                  setShareVideoId(shareableSelectedVideos[0].id);
                } else {
                  setShareAllOpen(true);
                }
              }}
              canShareVideo={canShareVideo}
              shareableCount={shareableSelectedVideos.length}
            />

            {/* Instruction Banner */}
            <InstructionBanner />
          </>
        )}

        {/* Video cards display grid; keyed by tab so the grid's load state and
            infinite-scroll sentinel reset when switching tabs */}
        <VideoGrid
          key={activeTab}
          tab={activeTab}
          isLoading={isLoading || isInitialLoad}
          paginatedVideos={videos}
          selectedVideos={selectedVideos}
          onVideoSelect={handleVideoSelection}
          hasMore={hasMore}
          onLoadMore={loadMore}
          hasActiveFilters={
            activeTab !== "unfetched" &&
            (!!createdFrom ||
              !!createdTo ||
              !!recordedFrom ||
              !!recordedTo ||
              !!searchTerm ||
              selectedUploaders.length > 0)
          }
          onResetFilters={handleResetFilters}
          onFetchRequested={markVideoFetching}
        />
      </Grid>

      {/* Single-video share (chip click). Kept mounted through the close
          animation; the id resolves the title. */}
      <ShareEvidenceModal
        open={!!shareVideoId}
        onClose={() => setShareVideoId(null)}
        evidenceId={shareVideoId ?? ""}
        videoTitle={shareVideo?.fileName}
      />

      {/* Batch share over every shareable selected video ("Share all"). */}
      <ShareMultipleEvidenceModal
        open={shareAllOpen}
        onClose={() => setShareAllOpen(false)}
        videos={shareableSelectedVideos.map((v) => ({
          id: v.id,
          fileName: v.fileName,
        }))}
      />
    </Container>
  );
};

export default VideoSelectionPage;
